import {Component, DestroyRef, type OnDestroy, type OnInit, inject} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import type {AppSession} from '../../../entities/modals/app-session.model'
import type {Subscription} from 'rxjs'
import {
  AlertController,
  IonAvatar,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
} from '@ionic/angular/standalone'
import {TranslatePipe, TranslateService} from '@ngx-translate/core'
import {SingleSignOnModeService} from '@cart/src/app/services/single-sign-on-mode.service'
import {config} from '@cart/src/config/config'
import {HeaderService} from '@services/header.service'
import {NavigationService} from '@services/navigation.service'
import {UserProfileService} from '@services/user-profile.service'
import {UserService} from '@services/user.service'
import {SessionService} from '@uve-solutions/composition/session'
import {UveTranslatePipe} from '../../../pipes/uve-translate.pipe'
import {FooterMenuComponent} from '../footer-menu/footer-menu'

@Component({
  selector: 'app-left-side-menu',
  imports: [
    IonAvatar,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
    FooterMenuComponent,
    TranslatePipe,
    UveTranslatePipe,
  ],
  templateUrl: './left-side-menu.component.html',
  styleUrl: './left-side-menu.component.scss',
})
export class LeftSideMenuComponent implements OnInit, OnDestroy {
  readonly userProfileService = inject(UserProfileService)

  readonly #destroyRef = inject(DestroyRef)
  readonly #sessionService = inject(SessionService)
  readonly #alertController = inject(AlertController)
  readonly #userService = inject(UserService)
  readonly #translate = inject(TranslateService)
  readonly #navigationService = inject(NavigationService)
  readonly #singleSignOnModeService = inject(SingleSignOnModeService)
  readonly #headerService = inject(HeaderService)

  user: AppSession
  fullName: string = null
  mobile: string = null
  isAnonymous = false
  hideSignUp = false
  howToUseAppActive = false
  showPowered: boolean
  isSingleSignOnMode = false
  showPrivacyURL: boolean
  showBlockAndArchive: boolean
  private userSubscription: Subscription

  async ngOnInit(): Promise<void> {
    this.userChangeListener()

    this.configure()
    this.init()
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe()
  }

  getUser() {
    this.user = this.#userService.getUser()
    if (!this.user) {
      return
    }
    this.fullName = this.userProfileService.getUserProfileFullName()
    this.mobile = this.user.mobileNumber
  }

  userChangeListener() {
    this.userSubscription = this.#userService
      .getUserSubscription()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(user => {
        this.user = user
        this.userProfileService.getUserProfileData(false).subscribe()
      })

    this.userProfileService
      .getProfileInfoUpdatedSubscription()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.init()
      })
  }

  async logoutConfirmation() {
    const alert = await this.#alertController.create({
      header: this.#translate.instant(!this.isAnonymous ? 'logout' : 'Login'),
      message: this.#translate.instant(!this.isAnonymous ? 'logoutMessage' : 'logoutMessageAnonymous'),
      buttons: [
        {
          text: this.#translate.instant('cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: this.#translate.instant('continue'),
          handler: () => {
            this.#sessionService.logout()
          },
        },
      ],
    })

    await alert.present()
  }

  goHome() {
    this.#headerService.openProvidersList().then()
  }

  goToProfile(event?) {
    this.menuNavigate('profile', event)
  }

  goToSetings(event?) {
    this.menuNavigate('settings', event)
  }

  goToSignupAnonymous(event?) {
    this.menuNavigate('signup-anonymous', event)
  }

  goToAppUsageHelper(event?) {
    this.menuNavigate('how-to', event)
  }

  init() {
    this.getUser()
    this.isSingleSignOnMode = this.#singleSignOnModeService.isSingleSignOnMode()
    this.isAnonymous = this.#userService.isAnonymous()
  }

  private menuNavigate(page: string, event = null) {
    if (event) {
      event.preventDefault()
    }
    this.#navigationService.navigateForward(page, null, false)
  }

  private configure() {
    this.howToUseAppActive = config.howToUseAppActive
    this.showPowered = config.signedByUve
    this.showPrivacyURL = config.showPrivacyURL
    this.showBlockAndArchive = config.conversationStatusFunctions
  }
}
